import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';

import useAuth from '@/hooks/useAuth';

import { getStepResource } from '@/utils/contentStep';
import { extractGameUrl } from '@/utils/document-utils';
import { isApiError } from '@/utils/request';

import { TrainingPlan } from '@/components/document';
import GenericError from '@/components/Error/GenericError';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import NoMessage from '@/components/NoMessage/NoMessage';

import { routes } from '@/constants';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { userPrefsAtom } from '@/atoms/userPrefs';
import PageNotFound404 from '@/routes/404/404';
import {
  isPlanResourceReference,
  resolvePlanResourceContentUrl,
  resolvePlanResourceDocumentType,
} from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/ResourceName/utils';
import { useDocProxyContext } from '@/routes/TrainingPlans/hooks/useDocProxyContext';
import { useTrackPlanResourceErrors } from '@/routes/TrainingPlans/hooks/useTrackPlanResourceErrors';

const DocumentProxy = () => {
  const track = useAmplitudeTrack();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const { language: userLanguage } = useAtomValue(userPrefsAtom);
  const [gameUrl, setGameUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);

  const {
    resourceReference,
    step,
    planId,
    stepId,
    stepStatus,
    userIds,
    isPending,
    isError,
    error,
    isPlanIdValid,
    stepNotFound,
    isTrainTogetherModeSessionInvalid,
    planAndAssignments,
    isPlanAccessDenied,
  } = useDocProxyContext();

  const locationNumber =
    auth?.locationsWithRoles.at(0)?.location.number ?? 'Unknown';

  useTrackPlanResourceErrors({
    planData: planAndAssignments,
    planId,
    language: userLanguage,
    locationNumber,
    planType: planAndAssignments?.plan?.type,
    enabled: !isPending && !!planAndAssignments,
  });

  const refType = getStepResource(step)?.type;
  const referenceId = getStepResource(step)?.id;
  const planResourceReference =
    resourceReference && isPlanResourceReference(resourceReference)
      ? resourceReference
      : undefined;
  const contentUrl = planResourceReference
    ? resolvePlanResourceContentUrl(planResourceReference)
    : undefined;
  const isGame = refType?.toUpperCase() === 'GAME';
  const isTridion = resourceReference?.sourceSystem === 'TRIDION';
  const documentType = resolvePlanResourceDocumentType(
    refType,
    planResourceReference?.format ?? '',
  );

  useEffect(() => {
    if (!isTrainTogetherModeSessionInvalid || !planId) {
      return;
    }

    datadogRum.addError(
      new Error(`Train Together session invalid for plan ${planId}`),
    );
    navigate(routes.plans.trainTogether({ planId }), { replace: true });
  }, [isTrainTogetherModeSessionInvalid, navigate, planId]);

  useEffect(() => {
    // Tridion games load HTML via useTridion; extracting the iframe src here
    // would fetch the same document twice.
    if (!isGame || !contentUrl || isTridion) {
      return;
    }
    (async () => {
      const htmlUrl = await extractGameUrl(contentUrl);
      setGameUrl(htmlUrl);
      setIsLoading(false);
    })();
  }, [isGame, contentUrl, isTridion]);

  useEffect(() => {
    if (!referenceId) {
      return;
    }

    const resourceName = resourceReference?.name;

    if (!resourceName) {
      return;
    }

    track(AmplitudeProductEvent.RESOURCE_VIEW, {
      resource_id: referenceId,
      resource_name: resourceName,
      source: 'plan',
      plan_id: planId,
      step_id: stepId,
      ...(documentType ? { content_type: documentType } : {}),
    });
  }, [referenceId, resourceReference, track, planId, stepId, documentType]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    datadogRum.addError(
      error instanceof Error ? error : new Error(JSON.stringify(error)),
    );
  }, [error, isError]);

  if (!isPlanIdValid) {
    return <PageNotFound404 />;
  }

  if (isPending) {
    return <LoadingOverlay isOpen />;
  }

  if (isTrainTogetherModeSessionInvalid) {
    return <LoadingOverlay isOpen />;
  }

  if (isPlanAccessDenied) {
    return <PageNotFound404 />;
  }

  if (isError && isApiError(error)) {
    if (error.status === 404) {
      return <PageNotFound404 />;
    }
    return <GenericError />;
  }

  if (isError) {
    return <GenericError />;
  }

  if (stepNotFound) {
    return <PageNotFound404 />;
  }

  if (isGame && !isTridion && contentUrl && isLoading) {
    return null;
  }

  const planWrapperClasses = `flex w-full ${
    isTridion ? 'max-w-[1152px]' : 'max-w-[840px]'
  }`;

  return referenceId ? (
    <div className={`pt-8 ${planWrapperClasses}`}>
      {planId && stepId && stepStatus && (
        <TrainingPlan
          planId={planId}
          refUrl={isTridion ? contentUrl : (gameUrl ?? contentUrl)}
          stepId={stepId}
          stepStatus={stepStatus}
          userIds={userIds}
          name={resourceReference?.name}
          isTridion={isTridion}
          lastModified={resourceReference?.updatedAt}
          documentType={documentType}
        />
      )}
    </div>
  ) : (
    <NoMessage message={t('TrainingPlans.noResourceReference')} />
  );
};

export default DocumentProxy;
